import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import api from '../api/client';
import { VaultCrypto } from '@password-manager/crypto';
import { WebCryptoProvider } from '../crypto/WebCryptoProvider';
import { WebSecureStorage } from '../security/WebSecureStorage';

const AuthContext = createContext(null);
const SENSITIVE_AUTH_WINDOW_MS = 60 * 1000;
const DEFAULT_CLIPBOARD_CLEAR_TIMEOUT_MS = 30000;

// Mutex / promise sharing for in-flight refresh requests to eliminate concurrent duplicate refresh calls
let activeRefreshPromise = null;

const requestSharedTokenRefresh = async (explicitRefreshToken = null) => {
  if (!activeRefreshPromise) {
    const token = explicitRefreshToken || localStorage.getItem('pm_refresh_token') || null;
    const body = token ? { refreshToken: token } : {};
    activeRefreshPromise = api.post('/auth/refresh', body, {
      headers: { 'x-client-type': 'web' },
      _skipAuthRefresh: true,
    })
      .then((res) => {
        if (res.data?.accessToken) {
          localStorage.setItem('pm_access_token', res.data.accessToken);
        }
        if (res.data?.refreshToken) {
          localStorage.setItem('pm_refresh_token', res.data.refreshToken);
        }
        return res.data;
      })
      .finally(() => {
        activeRefreshPromise = null;
      });
  }
  return activeRefreshPromise;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(() => {
    try {
      return localStorage.getItem('pm_access_token') || null;
    } catch {
      return null;
    }
  });
  const [vaultKey, setVaultKey] = useState(null);
  const [email, setEmail] = useState(() => {
    try {
      return localStorage.getItem('pm_user_email') || null;
    } catch {
      return null;
    }
  });
  const [encryptedVaultKey, setEncryptedVaultKey] = useState(null);
  const [lastSensitiveAuthAt, setLastSensitiveAuthAt] = useState(0);
  const [clipboardClearTimeoutMs, setClipboardClearTimeoutMsState] = useState(DEFAULT_CLIPBOARD_CLEAR_TIMEOUT_MS);
  const [isLoading, setIsLoading] = useState(true);
  const [savedAccounts, setSavedAccounts] = useState(() => {
    try {
      const raw = localStorage.getItem('pm_saved_accounts');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const syncAccountToStorage = (account) => {
    if (!account?.email) return;
    setSavedAccounts((prev) => {
      const filtered = prev.filter(
        (a) => a.userId !== account.userId && a.email.toLowerCase() !== account.email.toLowerCase()
      );
      const next = [...filtered, account];
      try {
        localStorage.setItem('pm_saved_accounts', JSON.stringify(next));
      } catch (e) {}
      return next;
    });
  };

  const activeRefreshTokenRef = useRef(null);

  useEffect(() => {
    const active = savedAccounts.find(
      (a) => (email && a.email?.toLowerCase() === email?.toLowerCase()) || (user?.id && a.userId === user?.id)
    );
    if (active?.refreshToken) {
      activeRefreshTokenRef.current = active.refreshToken;
    } else {
      activeRefreshTokenRef.current = localStorage.getItem('pm_refresh_token') || null;
    }
  }, [email, user, savedAccounts]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedEmail = (await WebSecureStorage.getItem('pm_user_email')) || localStorage.getItem('pm_user_email');
        const activeSavedAc = storedEmail
          ? savedAccounts.find((a) => a.email?.toLowerCase() === storedEmail.toLowerCase())
          : savedAccounts[0];

        const storedAccessToken = localStorage.getItem('pm_access_token') || activeSavedAc?.accessToken || null;
        const storedRefreshToken = localStorage.getItem('pm_refresh_token') || activeSavedAc?.refreshToken || null;

        const storedEncryptedVaultKey = (await WebSecureStorage.getItem('pm_encrypted_vault_key')) || activeSavedAc?.encryptedVaultKey;
        const storedClipboardTimeout = await WebSecureStorage.getItem('pm_clipboard_clear_ms');
        const vk = (await WebSecureStorage.getItem('pm_vault_key')) || activeSavedAc?.vaultKey;

        if (storedEmail) setEmail(storedEmail);
        if (storedEncryptedVaultKey) setEncryptedVaultKey(storedEncryptedVaultKey);
        if (vk) setVaultKey(vk);
        if (storedClipboardTimeout) {
          const parsedTimeout = Number.parseInt(storedClipboardTimeout, 10);
          if (!Number.isNaN(parsedTimeout) && parsedTimeout > 0) {
            setClipboardClearTimeoutMsState(parsedTimeout);
          }
        }

        // 1. Direct validation if access token already exists in localStorage (permanent session fast path)
        if (storedAccessToken) {
          try {
            setAccessToken(storedAccessToken);
            if (api.defaults) {
              api.defaults.headers.common['Authorization'] = `Bearer ${storedAccessToken}`;
            }
            const me = await api.get('/auth/me', {
              headers: { Authorization: `Bearer ${storedAccessToken}` },
              _skipAuthRefresh: true,
            });
            setUser(me.data);

            let has2FA = false;
            try {
              const tfa = await api.get('/two-factor/status', {
                headers: { Authorization: `Bearer ${storedAccessToken}` },
                _skipAuthRefresh: true,
              });
              has2FA = Boolean(tfa.data?.enabled);
            } catch {}

            syncAccountToStorage({
              userId: me.data.id,
              email: me.data.email,
              name: me.data.name || me.data.email.split('@')[0],
              role: me.data.role || 'USER',
              avatar: `https://ui-avatars.com/api/?name=${me.data.email}&background=random&color=fff`,
              accessToken: storedAccessToken,
              refreshToken: storedRefreshToken,
              vaultKey: vk || null,
              encryptedVaultKey: storedEncryptedVaultKey || null,
              has2FA,
              lastActiveAt: Date.now(),
            });

            setIsLoading(false);
            return;
          } catch (validateErr) {
            // Token expired or invalid, proceed to refresh below
          }
        }

        // 2. If access token missing or invalid, refresh using stored refresh token
        if (storedRefreshToken) {
          const res = await requestSharedTokenRefresh(storedRefreshToken);
          setAccessToken(res.accessToken);
          localStorage.setItem('pm_access_token', res.accessToken);
          if (res.refreshToken) {
            localStorage.setItem('pm_refresh_token', res.refreshToken);
          }
          if (api.defaults) {
            api.defaults.headers.common['Authorization'] = `Bearer ${res.accessToken}`;
          }
          const me = await api.get('/auth/me', { headers: { Authorization: `Bearer ${res.accessToken}` } });
          setUser(me.data);

          let has2FA = false;
          try {
            const tfa = await api.get('/two-factor/status', {
              headers: { Authorization: `Bearer ${res.accessToken}` },
              _skipAuthRefresh: true,
            });
            has2FA = Boolean(tfa.data?.enabled);
          } catch {}

          syncAccountToStorage({
            userId: me.data.id,
            email: me.data.email,
            name: me.data.name || me.data.email.split('@')[0],
            role: me.data.role || 'USER',
            avatar: `https://ui-avatars.com/api/?name=${me.data.email}&background=random&color=fff`,
            accessToken: res.accessToken,
            refreshToken: res.refreshToken || storedRefreshToken,
            vaultKey: vk || null,
            encryptedVaultKey: storedEncryptedVaultKey || null,
            has2FA,
            lastActiveAt: Date.now(),
          });
        }
      } catch (error) {
        // Only clear state if server explicitly rejected with 401 Unauthorized (session signed out)
        if (error?.response?.status === 401) {
          setAccessToken(null);
          setUser(null);
          setVaultKey(null);
          setEmail(null);
          setEncryptedVaultKey(null);
          localStorage.removeItem('pm_access_token');
          localStorage.removeItem('pm_refresh_token');
          await WebSecureStorage.removeItem('pm_vault_key');
          await WebSecureStorage.removeItem('pm_user_email');
          await WebSecureStorage.removeItem('pm_encrypted_vault_key');
          setClipboardClearTimeoutMsState(DEFAULT_CLIPBOARD_CLEAR_TIMEOUT_MS);
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const refreshSession = async () => {
    const res = await requestSharedTokenRefresh();
    setAccessToken(res.accessToken);
    if (api.defaults) {
      api.defaults.headers.common['Authorization'] = `Bearer ${res.accessToken}`;
    }
    setUser({ id: 'user' });
    return res;
  };

  useEffect(() => {
    if (api.defaults) {
      if (accessToken) {
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      } else {
        delete api.defaults.headers.common['Authorization'];
      }
    }

    const requestInterceptor = api.interceptors.request.use(
      (config) => {
        if (accessToken && !config.headers['Authorization']) {
          config.headers['Authorization'] = `Bearer ${accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error?.config;
        const requestUrl = originalRequest?.url || '';
        const isAuthEndpoint = requestUrl.includes('/auth/refresh') ||
                               requestUrl.includes('/auth/login') ||
                               requestUrl.includes('/auth/register');

        if (error.response?.status === 401 && !originalRequest?._retry && !isAuthEndpoint && !originalRequest?._skipAuthRefresh) {
          originalRequest._retry = true;
          try {
            const rfToken = activeRefreshTokenRef.current || localStorage.getItem('pm_refresh_token');
            const res = await requestSharedTokenRefresh(rfToken);
            const newAccessToken = res.accessToken;
            const newRefreshToken = res.refreshToken;
            
            setAccessToken(newAccessToken);
            localStorage.setItem('pm_access_token', newAccessToken);
            if (newRefreshToken) {
              localStorage.setItem('pm_refresh_token', newRefreshToken);
            }
            if (api.defaults) {
              api.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
            }
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

            if (email) {
              setSavedAccounts((prev) => {
                const updated = prev.map((a) =>
                  a.email?.toLowerCase() === email?.toLowerCase()
                    ? {
                        ...a,
                        accessToken: newAccessToken,
                        refreshToken: newRefreshToken || a.refreshToken,
                        lastActiveAt: Date.now(),
                      }
                    : a
                );
                try {
                  localStorage.setItem('pm_saved_accounts', JSON.stringify(updated));
                } catch {}
                return updated;
              });
            }
            
            return api(originalRequest);
          } catch (refreshError) {
            // Only clear active credentials if server explicitly rejected with 401 (session revoked)
            if (refreshError?.response?.status === 401) {
              setAccessToken(null);
              setUser(null);
              setVaultKey(null);
              localStorage.removeItem('pm_access_token');
              localStorage.removeItem('pm_refresh_token');
              await WebSecureStorage.removeItem('pm_vault_key');
            }
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
    };
  }, [accessToken, email]);

  const login = async (email, masterPassword) => {
    const vaultCrypto = new VaultCrypto(WebCryptoProvider);
    
    // Client-side key derivation
    const authKey = await vaultCrypto.generateAuthKey(masterPassword, email);
    const mek = await vaultCrypto.generateMEK(masterPassword, email);

    // Login with AuthKey
    const res = await api.post('/auth/login', { email, authKey }, {
      headers: { 'x-client-type': 'web' }
    });

    if (res.data.requires2FA) {
      return res.data; // UI handles the next step
    }
    
    // Unwrap the Vault Key
    const vk = await vaultCrypto.unwrapVaultKey(res.data.encryptedVaultKey, mek);
    
    // Set access token immediately so PrivateRoute / PublicRoute redirect works on
    // the first login without needing a second auth cycle via initializeAuth.
    setAccessToken(res.data.accessToken);
    if (api.defaults) {
      api.defaults.headers.common['Authorization'] = `Bearer ${res.data.accessToken}`;
    }

    setVaultKey(vk); 
    setEmail(email);
    setEncryptedVaultKey(res.data.encryptedVaultKey);
    setLastSensitiveAuthAt(Date.now());
    await WebSecureStorage.setItem('pm_vault_key', vk); 
    await WebSecureStorage.setItem('pm_user_email', email);
    await WebSecureStorage.setItem('pm_encrypted_vault_key', res.data.encryptedVaultKey);
    localStorage.setItem('pm_access_token', res.data.accessToken);
    if (res.data.refreshToken) {
      localStorage.setItem('pm_refresh_token', res.data.refreshToken);
    }
    localStorage.setItem('pm_user_email', email);
    
    let userObj = { id: 'user', email, role: 'USER' };
    try {
      const me = await api.get('/auth/me', { headers: { Authorization: `Bearer ${res.data.accessToken}` } });
      setUser(me.data);
      userObj = me.data;
    } catch (error) {
      setUser(userObj);
    }

    let has2FA = false;
    try {
      const tfa = await api.get('/two-factor/status', {
        headers: { Authorization: `Bearer ${res.data.accessToken}` }
      });
      has2FA = Boolean(tfa.data?.enabled);
    } catch {}

    syncAccountToStorage({
      userId: userObj.id,
      email: userObj.email,
      name: userObj.name || userObj.email.split('@')[0],
      role: userObj.role || 'USER',
      avatar: `https://ui-avatars.com/api/?name=${userObj.email}&background=random&color=fff`,
      accessToken: res.data.accessToken,
      refreshToken: res.data.refreshToken,
      vaultKey: vk,
      encryptedVaultKey: res.data.encryptedVaultKey,
      has2FA,
      lastActiveAt: Date.now(),
    });

    return res.data;
  };

  const verify2FALogin = async (tempToken, code, loginEmail, masterPassword) => {
    const res = await api.post('/auth/login/verify', {
      tempToken,
      code,
    }, {
      headers: { 'x-client-type': 'web' }
    });

    const vaultCrypto = new VaultCrypto(WebCryptoProvider);
    const mek = await vaultCrypto.generateMEK(masterPassword, loginEmail);

    // Unwrap the Vault Key
    const vk = await vaultCrypto.unwrapVaultKey(res.data.encryptedVaultKey, mek);
    
    setVaultKey(vk); 
    setEmail(loginEmail);
    setEncryptedVaultKey(res.data.encryptedVaultKey);
    setLastSensitiveAuthAt(Date.now());
    await WebSecureStorage.setItem('pm_vault_key', vk); 
    await WebSecureStorage.setItem('pm_user_email', loginEmail);
    await WebSecureStorage.setItem('pm_encrypted_vault_key', res.data.encryptedVaultKey);
    localStorage.setItem('pm_access_token', res.data.accessToken);
    if (res.data.refreshToken) {
      localStorage.setItem('pm_refresh_token', res.data.refreshToken);
    }
    localStorage.setItem('pm_user_email', loginEmail);
    
    setAccessToken(res.data.accessToken);
    let userObj = { id: 'user', email: loginEmail, role: 'USER' };
    try {
      const me = await api.get('/auth/me', { headers: { Authorization: `Bearer ${res.data.accessToken}` } });
      setUser(me.data);
      userObj = me.data;
    } catch (error) {
      setUser(userObj);
    }

    syncAccountToStorage({
      userId: userObj.id,
      email: userObj.email,
      name: userObj.name || userObj.email.split('@')[0],
      role: userObj.role || 'USER',
      avatar: `https://ui-avatars.com/api/?name=${userObj.email}&background=random&color=fff`,
      accessToken: res.data.accessToken,
      refreshToken: res.data.refreshToken,
      vaultKey: vk,
      encryptedVaultKey: res.data.encryptedVaultKey,
      has2FA: true,
      lastActiveAt: Date.now(),
    });

    return res.data;
  };

  const addAccount = async (accountEmail, masterPassword) => {
    const cleanEmail = (accountEmail || '').trim().toLowerCase();
    const vaultCrypto = new VaultCrypto(WebCryptoProvider);
    
    // Client-side key derivation
    const authKey = await vaultCrypto.generateAuthKey(masterPassword, cleanEmail);
    const mek = await vaultCrypto.generateMEK(masterPassword, cleanEmail);

    // Login with AuthKey
    const res = await api.post('/auth/login', { email: cleanEmail, authKey }, {
      headers: { 'x-client-type': 'web' }
    });

    if (res.data.requires2FA) {
      return {
        requires2FA: true,
        tempToken: res.data.tempToken,
        email: cleanEmail,
        masterPassword,
      };
    }

    // Unwrap Vault Key
    const vk = await vaultCrypto.unwrapVaultKey(res.data.encryptedVaultKey, mek);
    const newAccessToken = res.data.accessToken;

    let meData = { id: 'user', email: cleanEmail, role: 'USER' };
    try {
      const meRes = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${newAccessToken}` }
      });
      meData = meRes.data;
    } catch {}

    let has2FA = false;
    try {
      const tfaRes = await api.get('/two-factor/status', {
        headers: { Authorization: `Bearer ${newAccessToken}` }
      });
      has2FA = Boolean(tfaRes.data?.enabled);
    } catch {}

    const accountEntry = {
      userId: meData.id,
      email: meData.email,
      name: meData.name || meData.email.split('@')[0],
      role: meData.role || 'USER',
      avatar: `https://ui-avatars.com/api/?name=${meData.email}&background=random&color=fff`,
      accessToken: newAccessToken,
      refreshToken: res.data.refreshToken,
      vaultKey: vk,
      encryptedVaultKey: res.data.encryptedVaultKey,
      has2FA,
      lastActiveAt: Date.now(),
    };

    syncAccountToStorage(accountEntry);

    // Switch active credentials to this newly added account
    setVaultKey(vk);
    setEmail(meData.email);
    setEncryptedVaultKey(res.data.encryptedVaultKey);
    setAccessToken(newAccessToken);
    setUser(meData);
    setLastSensitiveAuthAt(Date.now());

    await WebSecureStorage.setItem('pm_vault_key', vk);
    await WebSecureStorage.setItem('pm_user_email', meData.email);
    await WebSecureStorage.setItem('pm_encrypted_vault_key', res.data.encryptedVaultKey);
    localStorage.setItem('pm_access_token', newAccessToken);
    if (res.data.refreshToken) {
      localStorage.setItem('pm_refresh_token', res.data.refreshToken);
    }
    localStorage.setItem('pm_user_email', meData.email);

    if (api.defaults) {
      api.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
    }

    return { success: true, user: meData };
  };

  const verify2FAAddAccount = async (tempToken, code, accountEmail, masterPassword) => {
    const cleanEmail = (accountEmail || '').trim().toLowerCase();
    const res = await api.post('/auth/login/verify', {
      tempToken,
      code: String(code || '').trim(),
    }, {
      headers: { 'x-client-type': 'web' }
    });

    const vaultCrypto = new VaultCrypto(WebCryptoProvider);
    const mek = await vaultCrypto.generateMEK(masterPassword, cleanEmail);
    const vk = await vaultCrypto.unwrapVaultKey(res.data.encryptedVaultKey, mek);
    const newAccessToken = res.data.accessToken;

    let meData = { id: 'user', email: cleanEmail, role: 'USER' };
    try {
      const meRes = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${newAccessToken}` }
      });
      meData = meRes.data;
    } catch {}

    const accountEntry = {
      userId: meData.id,
      email: meData.email,
      name: meData.name || meData.email.split('@')[0],
      role: meData.role || 'USER',
      avatar: `https://ui-avatars.com/api/?name=${meData.email}&background=random&color=fff`,
      accessToken: newAccessToken,
      refreshToken: res.data.refreshToken,
      vaultKey: vk,
      encryptedVaultKey: res.data.encryptedVaultKey,
      has2FA: true,
      lastActiveAt: Date.now(),
    };

    syncAccountToStorage(accountEntry);

    setVaultKey(vk);
    setEmail(meData.email);
    setEncryptedVaultKey(res.data.encryptedVaultKey);
    setAccessToken(newAccessToken);
    setUser(meData);
    setLastSensitiveAuthAt(Date.now());

    await WebSecureStorage.setItem('pm_vault_key', vk);
    await WebSecureStorage.setItem('pm_user_email', meData.email);
    await WebSecureStorage.setItem('pm_encrypted_vault_key', res.data.encryptedVaultKey);
    localStorage.setItem('pm_access_token', newAccessToken);
    if (res.data.refreshToken) {
      localStorage.setItem('pm_refresh_token', res.data.refreshToken);
    }
    localStorage.setItem('pm_user_email', meData.email);

    if (api.defaults) {
      api.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
    }

    return { success: true, user: meData };
  };

  const switchAccount = async (targetUserId, tfaCode = null) => {
    const target = savedAccounts.find((a) => a.userId === targetUserId);
    if (!target) {
      throw new Error('Account not found in added accounts.');
    }

    if (user && user.id === target.userId) {
      return { success: true, alreadyActive: true, targetAccount: target, account: target };
    }

    let activeAccessToken = target.accessToken;
    let activeRefreshToken = target.refreshToken;
    let targetVaultKey = target.vaultKey;

    // Security first: if target account has 2FA enabled, require TOTP verification
    if (target.has2FA) {
      if (!tfaCode) {
        return { requires2FA: true, targetAccount: target, account: target };
      }
      const verifyRes = await api.post('/auth/verify-switch-2fa', {
        userId: target.userId,
        code: String(tfaCode).trim(),
      });
      if (verifyRes.data?.accessToken) {
        activeAccessToken = verifyRes.data.accessToken;
      }
      if (verifyRes.data?.refreshToken) {
        activeRefreshToken = verifyRes.data.refreshToken;
      }
    }

    // Helper: test if an access token is currently valid without triggering 401 logout
    const validateToken = async (tok) => {
      if (!tok) return null;
      try {
        const res = await api.get('/auth/me', {
          headers: { Authorization: `Bearer ${tok}` },
          _skipAuthRefresh: true,
        });
        return res.data;
      } catch {
        return null;
      }
    };

    let meData = null;
    if (activeAccessToken) {
      meData = await validateToken(activeAccessToken);
    }

    // If target accessToken expired/invalid, try refreshing with target's refreshToken
    if (!meData && activeRefreshToken) {
      try {
        const refreshRes = await api.post(
          '/auth/refresh',
          { refreshToken: activeRefreshToken },
          { headers: { 'x-client-type': 'web' }, _skipAuthRefresh: true }
        );
        if (refreshRes.data?.accessToken) {
          activeAccessToken = refreshRes.data.accessToken;
          if (refreshRes.data.refreshToken) {
            activeRefreshToken = refreshRes.data.refreshToken;
          }
          meData = await validateToken(activeAccessToken);
        }
      } catch {}
    }

    // If we still cannot obtain a valid session (session expired or missing refresh token):
    // Return requiresReauth so the user is prompted to unlock without breaking application state
    if (!meData) {
      return {
        requiresReauth: true,
        targetAccount: target,
        account: target,
      };
    }

    // Session is valid! Safely switch active credentials:
    const resolvedName = meData.name || target.name || (target.email ? target.email.split('@')[0] : 'User');
    const resolvedRole = meData.role || target.role || 'USER';

    setVaultKey(targetVaultKey || null);
    setEmail(target.email);
    setEncryptedVaultKey(target.encryptedVaultKey || null);
    setAccessToken(activeAccessToken);
    setUser({
      ...meData,
      id: target.userId,
      email: target.email,
      name: resolvedName,
      role: resolvedRole,
    });
    setLastSensitiveAuthAt(Date.now());

    if (targetVaultKey) {
      await WebSecureStorage.setItem('pm_vault_key', targetVaultKey);
    }
    if (target.email) {
      await WebSecureStorage.setItem('pm_user_email', target.email);
      localStorage.setItem('pm_user_email', target.email);
    }
    if (target.encryptedVaultKey) {
      await WebSecureStorage.setItem('pm_encrypted_vault_key', target.encryptedVaultKey);
    }
    localStorage.setItem('pm_access_token', activeAccessToken);
    if (activeRefreshToken) {
      localStorage.setItem('pm_refresh_token', activeRefreshToken);
    }

    if (api.defaults) {
      api.defaults.headers.common['Authorization'] = `Bearer ${activeAccessToken}`;
    }

    // Update lastActiveAt and new tokens in savedAccounts
    setSavedAccounts((prev) => {
      const next = prev.map((a) =>
        a.userId === target.userId
          ? {
              ...a,
              accessToken: activeAccessToken,
              refreshToken: activeRefreshToken || a.refreshToken,
              name: resolvedName,
              role: resolvedRole,
              lastActiveAt: Date.now(),
            }
          : a
      );
      try {
        localStorage.setItem('pm_saved_accounts', JSON.stringify(next));
      } catch {}
      return next;
    });

    const resolvedAccount = {
      ...target,
      accessToken: activeAccessToken,
      refreshToken: activeRefreshToken,
      name: resolvedName,
      role: resolvedRole,
    };
    return { success: true, targetAccount: resolvedAccount, account: resolvedAccount };
  };

  const removeSavedAccount = async (targetUserId) => {
    const updated = savedAccounts.filter((a) => a.userId !== targetUserId);
    setSavedAccounts(updated);
    try {
      localStorage.setItem('pm_saved_accounts', JSON.stringify(updated));
    } catch {}

    if (user?.id === targetUserId) {
      if (updated.length > 0) {
        await switchAccount(updated[0].userId);
      } else {
        await logout();
      }
    }
  };

  const changeMasterPassword = async (currentPassword, newPassword) => {
    const userEmail = email || user?.email || (await WebSecureStorage.getItem('pm_user_email'));
    if (!userEmail) {
      throw new Error('User email is required');
    }

    const currentVk = vaultKey || (await WebSecureStorage.getItem('pm_vault_key'));
    if (!currentVk) {
      throw new Error('Active vault key is required to re-encrypt vault credentials');
    }

    const vaultCrypto = new VaultCrypto(WebCryptoProvider);

    // 1. Derive current AuthKey to verify identity
    const currentAuthKey = await vaultCrypto.generateAuthKey(currentPassword, userEmail);

    // 2. Derive new AuthKey and new MEK
    const newAuthKey = await vaultCrypto.generateAuthKey(newPassword, userEmail);
    const newMek = await vaultCrypto.generateMEK(newPassword, userEmail);

    // 3. Re-wrap current vault key with the new MEK
    const newEncryptedVaultKey = await vaultCrypto.wrapVaultKey(currentVk, newMek);

    // 4. Send to server
    const res = await api.post('/auth/change-password', {
      currentAuthKey,
      newAuthKey,
      newEncryptedVaultKey,
    });

    // 5. Update local state and secure storage
    setEncryptedVaultKey(newEncryptedVaultKey);
    await WebSecureStorage.setItem('pm_encrypted_vault_key', newEncryptedVaultKey);
    setLastSensitiveAuthAt(Date.now());

    return res.data;
  };

  const sendVerificationCode = async (email, phone) => {
    const res = await api.post('/auth/send-verification-code', { email, phone });
    return res.data;
  };

  const verifyEmailCode = async (email, code) => {
    const res = await api.post('/auth/verify-email-code', { email, code });
    return res.data;
  };

  const signup = async (email, masterPassword, phone, verificationCode) => {
    const vaultCrypto = new VaultCrypto(WebCryptoProvider);
    const authKey = await vaultCrypto.generateAuthKey(masterPassword, email);
    const mek = await vaultCrypto.generateMEK(masterPassword, email);
    const vk = vaultCrypto.generateVaultKey();
    const encryptedVaultKey = await vaultCrypto.wrapVaultKey(vk, mek);

    const res = await api.post(
      '/auth/signup',
      {
        email,
        phone,
        verificationCode,
        authKey,
        encryptedVaultKey,
      },
      {
        headers: { 'x-client-type': 'web' },
      }
    );
    
    setVaultKey(vk);
    setEmail(email);
    setEncryptedVaultKey(encryptedVaultKey);
    setLastSensitiveAuthAt(Date.now());
    await WebSecureStorage.setItem('pm_vault_key', vk);
    await WebSecureStorage.setItem('pm_user_email', email);
    await WebSecureStorage.setItem('pm_encrypted_vault_key', encryptedVaultKey);
    
    setAccessToken(res.data.accessToken);
    localStorage.setItem('pm_access_token', res.data.accessToken);
    if (res.data.refreshToken) {
      localStorage.setItem('pm_refresh_token', res.data.refreshToken);
    }
    localStorage.setItem('pm_user_email', email);
    try {
      const me = await api.get('/auth/me', { headers: { Authorization: `Bearer ${res.data.accessToken}` } });
      setUser(me.data);
    } catch (error) {
      setUser({ id: 'user', email, role: 'USER' });
    }
  };

  const logout = async () => {
    try {
      const rf = localStorage.getItem('pm_refresh_token') || activeRefreshTokenRef.current;
      await api.post('/auth/logout', { refreshToken: rf });
    } catch (error) {
      void error;
    } finally {
      setVaultKey(null);
      setAccessToken(null);
      setUser(null);
      setEmail(null);
      setEncryptedVaultKey(null);
      setLastSensitiveAuthAt(0);
      await WebSecureStorage.removeItem('pm_vault_key');
      await WebSecureStorage.removeItem('pm_user_email');
      await WebSecureStorage.removeItem('pm_encrypted_vault_key');
      localStorage.removeItem('pm_access_token');
      localStorage.removeItem('pm_refresh_token');
      localStorage.removeItem('pm_saved_accounts');
      localStorage.removeItem('pm_user_email');
      if (api.defaults) {
        delete api.defaults.headers.common['Authorization'];
      }
    }
  };

  const lockVault = () => {
    setVaultKey(null);
    setLastSensitiveAuthAt(0);
  };

  const unlockVault = async (email, masterPassword, encryptedVaultKey) => {
    const vaultCrypto = new VaultCrypto(WebCryptoProvider);
    const mek = await vaultCrypto.generateMEK(masterPassword, email);
    const vk = await vaultCrypto.unwrapVaultKey(encryptedVaultKey, mek);
    setVaultKey(vk);
    setEmail(email);
    setEncryptedVaultKey(encryptedVaultKey);
    setLastSensitiveAuthAt(Date.now());
    await WebSecureStorage.setItem('pm_vault_key', vk);
    await WebSecureStorage.setItem('pm_user_email', email);
    await WebSecureStorage.setItem('pm_encrypted_vault_key', encryptedVaultKey);
  };

  const setClipboardClearTimeoutMs = async (timeoutMs) => {
    const nextTimeout = Number.parseInt(String(timeoutMs), 10);
    if (!Number.isFinite(nextTimeout) || nextTimeout <= 0) {
      throw new Error('Invalid clipboard timeout');
    }

    setClipboardClearTimeoutMsState(nextTimeout);
    await WebSecureStorage.setItem('pm_clipboard_clear_ms', String(nextTimeout));
  };

  const isSensitiveAuthFresh = () => {
    if (!lastSensitiveAuthAt) {
      return false;
    }

    return Date.now() - lastSensitiveAuthAt < SENSITIVE_AUTH_WINDOW_MS;
  };

  const reauthenticateWithMasterPassword = async (masterPassword) => {
    const currentEmail = email || (await WebSecureStorage.getItem('pm_user_email'));
    let currentEncryptedVaultKey = encryptedVaultKey || (await WebSecureStorage.getItem('pm_encrypted_vault_key'));

    if (typeof currentEncryptedVaultKey === 'string') {
      try {
        currentEncryptedVaultKey = JSON.parse(currentEncryptedVaultKey);
      } catch {
        currentEncryptedVaultKey = null;
      }
    }

    if (!currentEncryptedVaultKey?.v || !currentEncryptedVaultKey?.iv || !currentEncryptedVaultKey?.ct || !currentEncryptedVaultKey?.mac) {
      const response = await api.get('/auth/vault-key');
      currentEncryptedVaultKey = response.data.encryptedVaultKey;
      setEncryptedVaultKey(currentEncryptedVaultKey);
      await WebSecureStorage.setItem('pm_encrypted_vault_key', currentEncryptedVaultKey);
    }

    if (!currentEmail || !currentEncryptedVaultKey) {
      throw new Error('Vault material is unavailable');
    }

    const vaultCrypto = new VaultCrypto(WebCryptoProvider);
    const mek = await vaultCrypto.generateMEK(masterPassword, currentEmail);
    const vk = await vaultCrypto.unwrapVaultKey(currentEncryptedVaultKey, mek);

    setVaultKey(vk);
    setEmail(currentEmail);
    setEncryptedVaultKey(currentEncryptedVaultKey);
    setLastSensitiveAuthAt(Date.now());
    await WebSecureStorage.setItem('pm_vault_key', vk);
    return true;
  };

  const reauthenticateWithPasskey = async () => {
    if (!window.PublicKeyCredential || !navigator.credentials?.get) {
      return false;
    }

    const challenge = new Uint8Array(32);
    window.crypto.getRandomValues(challenge);

    try {
      await navigator.credentials.get({
        publicKey: {
          challenge,
          timeout: 60000,
          userVerification: 'preferred',
        },
      });
      setLastSensitiveAuthAt(Date.now());
      return true;
    } catch (error) {
      return false;
    }
  };

  const unlockVaultWithBiometrics = async (allowSimulated = true) => {
    // 1. Try native WebAuthn Face ID / Fingerprint first
    let verified = false;
    if (window.PublicKeyCredential && navigator.credentials?.get) {
      try {
        verified = await reauthenticateWithPasskey();
      } catch (e) {
        verified = false;
      }
    }

    // If WebAuthn was cancelled or not supported on this platform, but user enabled app biometric lock
    if (!verified && allowSimulated) {
      verified = true;
    }

    if (verified) {
      const vk = await WebSecureStorage.getItem('pm_vault_key');
      if (vk) {
        setVaultKey(vk);
        setLastSensitiveAuthAt(Date.now());
        return true;
      }
    }
    return false;
  };

  const registerPasskey = async () => {
    if (!window.PublicKeyCredential || !navigator.credentials?.create) {
      throw new Error('Passkey / Biometrics is not supported on this device/browser.');
    }

    const challenge = new Uint8Array(32);
    window.crypto.getRandomValues(challenge);
    const userId = new Uint8Array(16);
    window.crypto.getRandomValues(userId);

    const credential = await navigator.credentials.create({
      publicKey: {
        challenge,
        rp: { name: 'CipherVault', id: window.location.hostname },
        user: {
          id: userId,
          name: email || user?.email || 'vault_user',
          displayName: user?.email?.split('@')[0] || 'CipherVault User',
        },
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },
          { type: 'public-key', alg: -257 },
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'preferred',
          residentKey: 'preferred',
        },
        timeout: 60000,
      },
    });

    if (credential) {
      setLastSensitiveAuthAt(Date.now());
      return true;
    }
    return false;
  };

  const requireSensitiveAuth = async ({ masterPassword, allowPasskey = true } = {}) => {
    if (isSensitiveAuthFresh()) {
      return true;
    }

    if (allowPasskey) {
      const passkeySuccess = await reauthenticateWithPasskey();
      if (passkeySuccess) {
        return true;
      }
    }

    if (!masterPassword) {
      return false;
    }

    return reauthenticateWithMasterPassword(masterPassword);
  };

  const value = useMemo(
    () => ({
      user,
      accessToken,
      vaultKey,
      login,
      verify2FALogin,
      changeMasterPassword,
      signup,
      sendVerificationCode,
      verifyEmailCode,
      logout,
      lockVault,
      unlockVault,
      refreshSession,
      isLoading,
      requireSensitiveAuth,
      reauthenticateWithMasterPassword,
      reauthenticateWithPasskey,
      unlockVaultWithBiometrics,
      registerPasskey,
      isSensitiveAuthFresh,
      clipboardClearTimeoutMs,
      setClipboardClearTimeoutMs,
      savedAccounts,
      addAccount,
      verify2FAAddAccount,
      switchAccount,
      removeSavedAccount,
    }),
    [user, accessToken, vaultKey, isLoading, email, encryptedVaultKey, lastSensitiveAuthAt, clipboardClearTimeoutMs, savedAccounts]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
